import { AuthenticatedGuard } from "@/components/authenticated-guard";
import type { ColumnProperties } from "@/components/non_registrant/dashboard-table";
import { DashboardTable } from "@/components/non_registrant/dashboard-table";
import PageHeader from "@/components/page-header";
import { TitleAccountUsers } from "@/components/title/title-account-users";
import { TitleEmptyState } from "@/components/title/title-empty-state";
import { TitleRequestStatusBadge } from "@/components/title/title-request-status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import LoadingScreen from "@/components/ui/loading-screen";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserType } from "@/constants";
import { titleAuthConfigured } from "@/lib/title-auth";
import { getTitleLoginConnection } from "@/lib/title-login-connection";
import {
  TITLE_REQUEST_STATUS_LABEL,
  type TitleAccountAccess,
  type TitleRequestSummary,
  getMyTitleAccount,
  listMyTitleRequests,
  titleRequestActiveLabel,
  titleRequestSummaryLine,
} from "@/lib/title-request-api";
import { subscribeTitleRequestChanges } from "@/lib/title-request-live-refresh";
import { cn, compareDateStrings, formatDate } from "@/lib/utils";
import { useAuth0 } from "@auth0/auth0-react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

type TitleRequestSection = "active" | "further-info" | "completed" | "users";

const TAB_CLASS =
  "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm";

/** A short list stays as tiles. More than this becomes a table. */
export const TITLE_REQUESTOR_LIST_THRESHOLD = 25;

const REQUESTOR_LIST_KEYS = [
  "address",
  "jurisdiction",
  "submitted",
  "status",
  "active",
] as const;

type RequestorListKey = (typeof REQUESTOR_LIST_KEYS)[number];

function getRequestorListColumnProperties(
  accessorKey: RequestorListKey,
): ColumnProperties<TitleRequestSummary, RequestorListKey> {
  switch (accessorKey) {
    case "address":
      return {
        displayName: "Address",
        defaultVisible: true,
        getValue: (row) => titleRequestSummaryLine(row),
      };
    case "jurisdiction":
      return {
        displayName: "Jurisdiction",
        defaultVisible: true,
        getValue: (row) => row.jurisdiction_name ?? "",
      };
    case "submitted":
      return {
        displayName: "Submitted",
        defaultVisible: true,
        allowBlankFilter: false,
        filterType: "dateRange",
        getValue: (row) => formatDate(row.created_at),
        getFilterValue: (row) => row.created_at,
        sortingFn: (rowA, rowB) =>
          compareDateStrings(
            rowA.original.created_at,
            rowB.original.created_at,
          ),
      };
    case "status":
      return {
        displayName: "Status",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row) => TITLE_REQUEST_STATUS_LABEL[row.status],
        renderCell: (row) => <TitleRequestStatusBadge status={row.status} />,
      };
    case "active":
      return {
        displayName: "Active",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row) => titleRequestActiveLabel(row.is_active),
      };
  }
}

function requestsForSection(
  rows: TitleRequestSummary[],
  section: TitleRequestSection,
): TitleRequestSummary[] {
  if (section === "active") {
    return rows.filter((row) => row.is_active !== false);
  }
  if (section === "further-info") {
    return rows.filter((row) => row.status === "REQUIRES_FURTHER_INFO");
  }
  if (section === "completed") {
    return rows.filter((row) => row.is_active === false);
  }
  return [];
}

function emptySectionMessage(section: TitleRequestSection): string {
  if (section === "further-info") {
    return "No requests require further information.";
  }
  if (section === "completed") return "No completed requests.";
  return "No active requests.";
}

function RequestList() {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [rows, setRows] = useState<TitleRequestSummary[]>([]);
  const [account, setAccount] = useState<TitleAccountAccess | null>(null);
  const [section, setSection] = useState<TitleRequestSection>("active");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const token = await getAccessTokenSilently();
    const requests = await listMyTitleRequests(token);
    let account: TitleAccountAccess | null = null;
    try {
      account = await getMyTitleAccount(token);
    } catch {
      account = null;
    }
    return { requests: requests.requests, account };
  }, [getAccessTokenSilently]);

  useEffect(() => {
    let cancelled = false;
    void load()
      .then((result) => {
        if (!cancelled) {
          setRows(result.requests);
          setAccount(result.account);
          setError(null);
        }
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : "Could not load");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const showUsers = Boolean(account?.is_admin);

  useEffect(() => {
    if (!showUsers && section === "users") setSection("active");
  }, [showUsers, section]);

  useEffect(() => {
    let cancelled = false;
    const unsubscribe = subscribeTitleRequestChanges(() => {
      void load()
        .then((result) => {
          if (!cancelled) {
            setRows(result.requests);
            setAccount(result.account);
            setError(null);
          }
        })
        .catch(() => undefined);
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [load]);

  if (loading) {
    return <LoadingScreen message="Loading title requests…" />;
  }

  const furtherInfoCount = rows.filter(
    (row) => row.status === "REQUIRES_FURTHER_INFO",
  ).length;
  const visibleRows = requestsForSection(rows, section);
  const useTable = visibleRows.length > TITLE_REQUESTOR_LIST_THRESHOLD;

  return (
    <div
      className={cn(
        "container mx-auto flex w-full min-w-[375px] flex-col gap-6",
        useTable ? "max-w-none" : "max-w-3xl",
      )}
    >
      <PageHeader
        title="Title requests"
        subtitle="Review your submitted requests and Hera's responses."
        hideBackButton
        action={
          rows.length > 0 ? (
            <Button asChild>
              <Link to="/title/new">New Request...</Link>
            </Button>
          ) : null
        }
      />
      {error ? <p className="text-destructive">{error}</p> : null}
      <Tabs
        value={section}
        onValueChange={(value) => setSection(value as TitleRequestSection)}
      >
        <TabsList className="h-auto max-w-full flex-wrap justify-start bg-card [&_[data-slot=tabs-trigger]]:h-8 [&_[data-slot=tabs-trigger]]:flex-none">
          <TabsTrigger className={TAB_CLASS} value="active">
            All Active Requests
          </TabsTrigger>
          <TabsTrigger
            className={`${TAB_CLASS} data-[state=active]:bg-warning data-[state=active]:text-warning-foreground`}
            value="further-info"
          >
            <span className="flex items-center gap-1.5">
              Requires Further Information
              {furtherInfoCount > 0 ? (
                <Badge variant="warning" className="h-5 px-1.5">
                  {furtherInfoCount}
                </Badge>
              ) : null}
            </span>
          </TabsTrigger>
          <TabsTrigger className={TAB_CLASS} value="completed">
            Completed Requests
          </TabsTrigger>
          {showUsers ? (
            <TabsTrigger className={TAB_CLASS} value="users">
              Users
            </TabsTrigger>
          ) : null}
        </TabsList>
      </Tabs>
      {section === "users" && account ? (
        <TitleAccountUsers
          currentAccountId={account.id}
          isPrimary={account.primary_id == null}
        />
      ) : null}
      {section !== "users" && !error && rows.length === 0 ? (
        <TitleEmptyState />
      ) : null}
      {section !== "users" && rows.length > 0 && visibleRows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {emptySectionMessage(section)}
        </p>
      ) : null}
      {section !== "users" && visibleRows.length > 0 && !useTable ? (
        <Card className="overflow-hidden">
          <ul className="divide-y">
            {visibleRows.map((row) => (
              <li key={row.id}>
                <Link
                  to="/title/requests/$requestId"
                  params={{ requestId: row.id }}
                  className="flex items-center justify-between gap-4 px-4 py-4 transition-colors hover:bg-muted/40 md:px-6"
                >
                  <span className="font-medium">
                    <span className="block">
                      {titleRequestSummaryLine(row)}
                    </span>
                    <span className="block text-sm text-muted-foreground">
                      {[
                        row.jurisdiction_name?.trim(),
                        formatDate(row.created_at),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </span>
                  <TitleRequestStatusBadge status={row.status} />
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
      {section !== "users" && visibleRows.length > 0 && useTable ? (
        <DashboardTable
          data={visibleRows}
          accessorKeys={REQUESTOR_LIST_KEYS}
          getColumnProperties={getRequestorListColumnProperties}
          getRowId={(row) => row.id}
          tablePersistenceKey="title-requestor-requests"
          initialSortColumn="submitted"
          initialSortDirection="desc"
          lockToViewport={false}
          onRowClick={(requestId) => {
            void navigate({
              to: "/title/requests/$requestId",
              params: { requestId },
            });
          }}
        />
      ) : null}
    </div>
  );
}

export const Route = createFileRoute("/title/requests")({
  component: function TitleRequestsPage() {
    if (!titleAuthConfigured()) {
      return <p>Log in is not configured for title requests.</p>;
    }
    return (
      <AuthenticatedGuard
        allowedUserTypes={[UserType.TITLE_REQUESTOR, UserType.OPERATOR]}
        loginConnection={getTitleLoginConnection()}
      >
        <RequestList />
      </AuthenticatedGuard>
    );
  },
});
